import { SignClient } from '@walletconnect/sign-client'
import QRCodeModal from '@walletconnect/qrcode-modal'
import { getSdkError } from '@walletconnect/utils'

class LeapWalletConnectService {
  constructor() {
    this.signClient = null
    this.session = null
    this.initialized = false
    this.projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || "4c851542b167151ec9ef000be816eda2"
    this.connectPromise = null
  }

  async init() {
    if (this.initialized) return

    try {
      this.signClient = await SignClient.init({
        projectId: this.projectId,
        metadata: {
          name: "Coded Estate",
          description: "Book stays, unlock rewards, and explore crypto travel",
          url: window.location.origin,
          icons: [`${window.location.origin}/logo192.png`],
          verifyUrl: "https://verify.walletconnect.org/"
        }
      })

      this.setupEventListeners()
      this.initialized = true
      console.log('LeapWalletConnect initialized successfully')
    } catch (error) {
      console.error('Failed to initialize LeapWalletConnect:', error)
      throw error
    }
  }

  setupEventListeners() {
    if (!this.signClient) return

    this.signClient.on('session_event', ({ event, chainId }) => {
      console.log('Session event:', event, chainId)
    })

    this.signClient.on('session_update', ({ topic, params }) => {
      console.log('Session updated:', topic, params)
      const { namespaces } = params
      const session = this.signClient.session.get(topic)
      this.session = { ...session, namespaces }
    })

    this.signClient.on('session_delete', () => {
      console.log('Session deleted')
      this.session = null
      QRCodeModal.close()
    })

    this.signClient.on('session_approve', (session) => {
      console.log('Session approved:', session)
      this.session = session
    })
  }

  isMobile() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera
    return /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase())
  }

  // Check if we're already inside Leap Wallet's browser
  isInLeapWalletBrowser() {
    return !!window.leap && this.isMobile()
  }

  // Open Leap Wallet app with the current page URL
  openLeapWalletApp() {
    console.log('Opening Leap Wallet app with current page')
    
    try {
      // Use the working universal link to open Leap Wallet app
      window.open("https://leapcosmoswallet.page.link/ynM9cmrHK1KitqgMA", "_blank")
      
      return true
    } catch (error) {
      console.warn('Failed to open Leap Wallet app:', error)
      return false
    }
  }

  async connect() {
    if (!this.initialized) {
      await this.init()
    }

    if (this.connectPromise) {
      return this.connectPromise
    }

    this.connectPromise = this._doConnect()
    
    try {
      const result = await this.connectPromise
      return result
    } finally {
      this.connectPromise = null
    }
  }

  async _doConnect() {
    try {
      // If we're on mobile but NOT in Leap Wallet's browser, just open the app
      if (this.isMobile() && !this.isInLeapWalletBrowser()) {
        console.log('Mobile device detected - opening Leap Wallet app')
        
        const appOpened = this.openLeapWalletApp()
        if (!appOpened) {
          throw new Error('Failed to open Leap Wallet app')
        }
        
        // Return a special response indicating the app was opened
        throw new Error('redirected_to_app')
      }

      // If we're in Leap Wallet's browser, use direct connection
      if (this.isInLeapWalletBrowser()) {
        console.log('In Leap Wallet browser - using direct connection')
        
        // Use direct wallet connection since we're in Leap's browser
        if (!window.leap) {
          throw new Error('Leap wallet not available in browser')
        }

        try {
          const chainId = 'cataclysm-1'
          
          // Enable the wallet for the specific chain
          await window.leap.enable(chainId)
          
          // Get the offline signer for the chain
          const offlineSigner = window.leap.getOfflineSigner(chainId)
          
          // Get accounts from the offline signer
          const accounts = await offlineSigner.getAccounts()
          
          if (accounts && accounts.length > 0) {
            return {
              success: true,
              address: accounts[0].address,
              session: null, // No WalletConnect session needed
              accounts: accounts
            }
          } else {
            throw new Error('No accounts found in Leap Wallet')
          }
        } catch (leapError) {
          console.error('Leap direct connection error:', leapError)
          throw new Error(`Leap Wallet connection failed: ${leapError.message}`)
        }
      }

      // Desktop flow - use WalletConnect with QR code
      if (!this.isMobile()) {
        console.log('Desktop device - using WalletConnect QR code')
        
        const { uri, approval } = await this.signClient.connect({
          requiredNamespaces: {
            cosmos: {
              methods: [
                'cosmos_getAccounts',
                'cosmos_signAmino',
                'cosmos_signDirect'
              ],
              chains: [
                'cosmos:cataclysm-1',
                'cosmos:cosmoshub-4'
              ],
              events: ['chainChanged', 'accountsChanged']
            }
          },
          optionalNamespaces: {
            cosmos: {
              methods: [
                'cosmos_signTransaction',
                'cosmos_sendTransaction'
              ],
              chains: [
                'cosmos:cataclysm-1',
                'cosmos:cataclysm-1'
              ],
              events: []
            }
          }
        })

        if (uri) {
          console.log('WalletConnect URI generated:', uri)

          // Desktop: Show QR code modal
          QRCodeModal.open(uri, () => {
            console.log('QR Code modal closed by user')
            throw new Error('User closed QR code modal')
          })

          // Wait for session approval with timeout
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Connection timeout after 60 seconds')), 60000)
          })

          const session = await Promise.race([approval(), timeoutPromise])
          
          QRCodeModal.close()
          this.session = session
          console.log('Leap Wallet connected successfully:', session)

          // Extract account address
          const accounts = session.namespaces.cosmos?.accounts || []
          if (accounts.length > 0) {
            const address = accounts[0].split(':')[2]
            return {
              success: true,
              address,
              session,
              accounts
            }
          } else {
            throw new Error('No accounts found in session')
          }
        } else {
          throw new Error('Failed to generate connection URI')
        }
      }

      throw new Error('Unsupported connection method')
      
    } catch (error) {
      console.error('Leap Wallet connection failed:', error)
      QRCodeModal.close()
      
      if (error.message === 'redirected_to_app') {
        // Special case - app was opened, this is not really an error
        throw new Error('Please continue the connection process in the Leap Wallet app')
      } else if (error.message.includes('User closed QR code modal')) {
        throw new Error('Connection cancelled by user')
      } else if (error.message.includes('timeout')) {
        throw new Error('Connection timed out. Please try again.')
      } else if (error.message.includes('redirect')) {
        throw new Error('Unable to open Leap Wallet app. Please ensure it is installed.')
      }
      
      throw error
    }
  }

  async disconnect() {
    if (!this.session || !this.signClient) {
      console.log('No active session to disconnect')
      return { success: true }
    }

    try {
      await this.signClient.disconnect({
        topic: this.session.topic,
        reason: getSdkError('USER_DISCONNECTED')
      })
      
      this.session = null
      QRCodeModal.close()
      console.log('Leap Wallet disconnected successfully')
      
      return { success: true }
    } catch (error) {
      console.error('Error disconnecting Leap Wallet:', error)
      return { success: false, error: error.message }
    }
  }

  getSession() {
    return this.session
  }

  isConnected() {
    return !!this.session || this.isInLeapWalletBrowser()
  }

  getAccounts() {
    if (!this.session) return []
    
    const accounts = this.session.namespaces.cosmos?.accounts || []
    return accounts.map(account => account.split(':')[2])
  }

  async signMessage(message, address) {
    if (this.isInLeapWalletBrowser()) {
      // Use direct Leap API for signing
      try {
        const chainId = 'cataclysm-1'
        const offlineSigner = window.leap.getOfflineSigner(chainId)
        const result = await offlineSigner.signAmino(address, message)
        return result
      } catch (error) {
        console.error('Error signing message with Leap browser:', error)
        throw error
      }
    }

    if (!this.session || !this.signClient) {
      throw new Error('No active session')
    }

    try {
      const result = await this.signClient.request({
        topic: this.session.topic,
        chainId: 'cosmos:cataclysm-1',
        request: {
          method: 'cosmos_signAmino',
          params: {
            signerAddress: address,
            signDoc: message
          }
        }
      })

      return result
    } catch (error) {
      console.error('Error signing message:', error)
      throw error
    }
  }
}

// Create singleton instance
export const leapWalletConnectService = new LeapWalletConnectService()

// Export convenience function
export const connectLeapWallet = async () => {
  try {
    const result = await leapWalletConnectService.connect()
    return result
  } catch (error) {
    console.error('Failed to connect Leap Wallet:', error)
    throw error
  }
}

export const disconnectLeapWallet = async () => {
  try {
    const result = await leapWalletConnectService.disconnect()
    return result
  } catch (error) {
    console.error('Failed to disconnect Leap Wallet:', error)
    throw error
  }
}